import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

import { Permissions } from "../../../../app/enums";
import { usePermissionCheck } from "../../../../app/common/helper/Permissions";
import { MatchForExtractState } from "../../../../models/DataMatchContext";
import { DataMatchPair, DataMatchPersonSnapshot, DataMatchRunResult } from "../../../../models/DataMatchResult";
import { ServiceManager } from "../../../../services/ServiceManager";
import {
    matchColumnPlaceholderValue,
    newMatchRowId,
    type MatchFilterRow,
} from "./column-options";
import {
    centralRowKey,
    clonePair,
    cloneRunResult,
    mergeDataMatchRunWithManualLinks,
    mergeManualLinksIntoResult,
    pairRowKey,
    providerRowKey,
    syncManualPairsPreserveRefToRunResult,
    undoMatchBatch,
} from "./data-match-utils";
import {
    buildCompletePayload,
    buildDefaultMatchRowsState,
    cloneMatchRows,
    type MatchUndoFrame,
} from "./data-match-helpers";

export interface UseDataMatchOptions {
    match: MatchForExtractState;
    establishmentId: string;
    onBack: () => void;
}

export function useDataMatch({ match, establishmentId, onBack }: UseDataMatchOptions) {
    const [matchRows, setMatchRows] = useState<MatchFilterRow[]>([]);
    const [matchRunning, setMatchRunning] = useState(false);
    const [runResult, setRunResult] = useState<DataMatchRunResult | null>(null);
    const [hasCompletedMatchAttempt, setHasCompletedMatchAttempt] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [dataMatchRunKey, setDataMatchRunKey] = useState(0);
    const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
    const [matchRunHistoryStack, setMatchRunHistoryStack] = useState<MatchUndoFrame[]>([]);
    const [lastMatchBatch, setLastMatchBatch] = useState<DataMatchPair[]>([]);
    const [selectedCentral, setSelectedCentral] = useState<DataMatchPersonSnapshot | null>(null);
    const [selectedProvider, setSelectedProvider] = useState<DataMatchPersonSnapshot | null>(null);
    const [hasManualLinks, setHasManualLinks] = useState(false);
    const [completeModalOpen, setCompleteModalOpen] = useState(false);
    const [abandonModalOpen, setAbandonModalOpen] = useState(false);
    const [completeSubmitting, setCompleteSubmitting] = useState(false);

    const runResultRef = useRef<DataMatchRunResult | null>(null);
    const matchRowsRef = useRef<MatchFilterRow[]>([]);
    const matchRunHistoryStackRef = useRef<MatchUndoFrame[]>([]);
    const filtersBaselineAfterLastSuccessRef = useRef<MatchFilterRow[]>([]);
    const filtersBeforeLastPerformRef = useRef<MatchFilterRow[]>([]);
    const manualPairsPreserveRef = useRef<DataMatchPair[]>([]);

    const canEditEstablishments = usePermissionCheck(Permissions.CanEditEstablishments);

    // ─── Ref syncs ──────────────────────────────────────────────────────────────

    useEffect(() => { runResultRef.current = runResult; }, [runResult]);
    useEffect(() => { matchRowsRef.current = matchRows; }, [matchRows]);
    useEffect(() => { matchRunHistoryStackRef.current = matchRunHistoryStack; }, [matchRunHistoryStack]);

    // ─── Initial load ───────────────────────────────────────────────────────────

    useEffect(() => {
        const initialRows = buildDefaultMatchRowsState();
        setMatchRows(initialRows);
        const baseline = cloneMatchRows(initialRows);
        filtersBaselineAfterLastSuccessRef.current = baseline;
        filtersBeforeLastPerformRef.current = baseline;
        setRunResult(null);
        setHasCompletedMatchAttempt(false);
        setLastMatchBatch([]);
        setSelectedCentral(null);
        setSelectedProvider(null);
        manualPairsPreserveRef.current = [];
        matchRunHistoryStackRef.current = [];
        setMatchRunHistoryStack([]);
        setCompleteModalOpen(false);
        setAbandonModalOpen(false);
        setCompleteSubmitting(false);

        let cancelled = false;
        (async () => {
            setPreviewLoading(true);
            try {
                const resp = await ServiceManager.EstablishmentService.GetDataMatchPreview(establishmentId, match.clientID);
                if (cancelled) return;
                if (axios.isAxiosError(resp)) {
                    const d = resp.response?.data;
                    const msg =
                        typeof d === "string"
                            ? d
                            : (d as { title?: string; detail?: string })?.title ??
                            (d as { title?: string; detail?: string })?.detail ??
                            "Could not load records for data match.";
                    ServiceManager.ToastService.showError(msg);
                    return;
                }
                const data = (resp as { data?: DataMatchRunResult })?.data;
                if (data) {
                    setRunResult(data);
                }
            } catch {
                if (!cancelled) {
                    ServiceManager.ToastService.showError("Could not load records for data match.");
                }
            } finally {
                if (!cancelled) {
                    setPreviewLoading(false);
                }
            }
        })();

        return () => { cancelled = true; };
    }, [establishmentId, match.clientID, match.misConfigurationID, match.misType]);

    // ─── Match filter row actions ───────────────────────────────────────────────

    const updateRow = (id: string, patch: Partial<Pick<MatchFilterRow, "centralColumn" | "providerColumn" | "matchScore">>) => {
        setMatchRows((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    };

    const removeRow = (id: string) => {
        setMatchRows((rows) => (rows.length <= 1 ? rows : rows.filter((r) => r.id !== id)));
    };

    const addRow = () => {
        setMatchRows((rows) => [
            ...rows,
            {
                id: newMatchRowId(),
                centralColumn: matchColumnPlaceholderValue,
                providerColumn: matchColumnPlaceholderValue,
                matchScore: 100,
            },
        ]);
    };

    // ─── Perform data match ─────────────────────────────────────────────────────

    const performDataMatch = useCallback(async () => {
        if (matchRows.length === 0) {
            ServiceManager.ToastService.showWarning("Add at least one match rule.");
            return;
        }
        const hasIncompleteRule = matchRows.some(
            (r) => !String(r.centralColumn ?? "").trim() || !String(r.providerColumn ?? "").trim()
        );
        if (hasIncompleteRule) {
            ServiceManager.ToastService.showWarning("Select MIS Central and provider columns for each match rule.");
            return;
        }
        const filtersBeforeThisRun = cloneMatchRows(filtersBaselineAfterLastSuccessRef.current);
        filtersBeforeLastPerformRef.current = filtersBeforeThisRun;
        setMatchRunning(true);
        try {
            const resp = await ServiceManager.EstablishmentService.RunDataMatch(establishmentId, {
                clientId: match.clientID,
                rules: matchRows.map((r) => ({
                    centralField: r.centralColumn,
                    providerField: r.providerColumn,
                    thresholdPercent: r.matchScore,
                })),
            });
            if (axios.isAxiosError(resp)) {
                const d = resp.response?.data;
                const msg =
                    typeof d === "string"
                        ? d
                        : (d as { title?: string; detail?: string })?.title ??
                        (d as { title?: string; detail?: string })?.detail ??
                        "Data match could not be completed.";
                ServiceManager.ToastService.showError(msg);
                return;
            }
            const data = (resp as { data?: DataMatchRunResult })?.data;
            if (data) {
                const previous = runResultRef.current;
                if (previous) {
                    setMatchRunHistoryStack((h) => {
                        const next: MatchUndoFrame[] = [
                            ...h,
                            {
                                runResult: cloneRunResult(previous),
                                matchRows: cloneMatchRows(filtersBeforeThisRun),
                            },
                        ];
                        matchRunHistoryStackRef.current = next;
                        return next;
                    });
                }
                syncManualPairsPreserveRefToRunResult(manualPairsPreserveRef, previous);
                const merged = mergeDataMatchRunWithManualLinks(data, manualPairsPreserveRef.current);
                setRunResult(merged);
                setSelectedCentral(null);
                setSelectedProvider(null);
                setHasCompletedMatchAttempt(true);
                setLastMatchBatch(data.matchedPairs.map(clonePair));
                setDataMatchRunKey((k) => k + 1);
                filtersBaselineAfterLastSuccessRef.current = cloneMatchRows(matchRowsRef.current);
                const nAuto = data.matchedPairs.length;
                const nTotal = merged.matchedPairs.length;
                if (nAuto === 0) {
                    if (nTotal === 0) {
                        ServiceManager.ToastService.showInfo("No accounts matched");
                    } else {
                        ServiceManager.ToastService.showInfo("No new automatic matches; manual links preserved.");
                    }
                } else {
                    ServiceManager.ToastService.showSuccess(`${nAuto} account(s) matched by rules (${nTotal} total in Matched).`);
                }
            } else {
                ServiceManager.ToastService.showError("Data match could not be completed.");
            }
        } catch {
            ServiceManager.ToastService.showError("Data match could not be completed.");
        } finally {
            setMatchRunning(false);
        }
    }, [establishmentId, match.clientID, matchRows]);

    // ─── Unmatch / undo ─────────────────────────────────────────────────────────

    const unmatchPairAt = useCallback((index: number) => {
        setRunResult((prev) => {
            if (!prev || index < 0 || index >= prev.matchedPairs.length) return prev;
            const pair = prev.matchedPairs[index];
            const k = pairRowKey(pair);
            manualPairsPreserveRef.current = manualPairsPreserveRef.current.filter((p) => pairRowKey(p) !== k);
            const matchedPairs = prev.matchedPairs.filter((_, i) => i !== index);
            return {
                ...prev,
                matchedCount: matchedPairs.length,
                matchedPairs,
                unmatchedCentral: [...prev.unmatchedCentral, pair.central],
                unmatchedProvider: [...prev.unmatchedProvider, pair.provider],
            };
        });
    }, []);

    const undoLastMatch = useCallback(() => {
        setSelectedCentral(null);
        setSelectedProvider(null);
        const h = matchRunHistoryStackRef.current;
        if (h.length > 0) {
            const frame = h[h.length - 1];
            const restored = cloneRunResult(frame.runResult);
            const restoredRows = cloneMatchRows(frame.matchRows);
            const nextStack = h.slice(0, -1);
            matchRunHistoryStackRef.current = nextStack;
            setMatchRunHistoryStack(nextStack);
            setRunResult(restored);
            syncManualPairsPreserveRefToRunResult(manualPairsPreserveRef, restored);
            setHasManualLinks(manualPairsPreserveRef.current.length > 0);
            setHasCompletedMatchAttempt(nextStack.length > 0);
            setMatchRows(restoredRows);
            filtersBaselineAfterLastSuccessRef.current = cloneMatchRows(restoredRows);
            filtersBeforeLastPerformRef.current = cloneMatchRows(restoredRows);
            setLastMatchBatch(restored.matchedPairs.map(clonePair));
            setDataMatchRunKey((k) => k + 1);
            return;
        }
        setLastMatchBatch((batch) => {
            if (batch.length === 0) return batch;
            const revertedFilters = cloneMatchRows(filtersBeforeLastPerformRef.current);
            setMatchRows(revertedFilters);
            filtersBaselineAfterLastSuccessRef.current = cloneMatchRows(revertedFilters);
            filtersBeforeLastPerformRef.current = cloneMatchRows(revertedFilters);
            setRunResult((prev) => {
                if (!prev) return prev;
                const next = undoMatchBatch(prev, batch);
                syncManualPairsPreserveRefToRunResult(manualPairsPreserveRef, next);
                setHasManualLinks(manualPairsPreserveRef.current.length > 0);
                return next;
            });
            return [];
        });
    }, []);

    const canUndoLastMatch = matchRunHistoryStack.length > 0 || lastMatchBatch.length > 0;

    // ─── Manual link ────────────────────────────────────────────────────────────

    const showManualLinkModal = Boolean(selectedCentral && selectedProvider);

    const handleSelectCentral = useCallback((row: DataMatchPersonSnapshot) => {
        setSelectedCentral((prev) => {
            const key = centralRowKey(row);
            if (prev && centralRowKey(prev) === key) return null;
            return row;
        });
    }, []);

    const handleSelectProvider = useCallback((row: DataMatchPersonSnapshot) => {
        setSelectedProvider((prev) => {
            const key = providerRowKey(row);
            if (prev && providerRowKey(prev) === key) return null;
            return row;
        });
    }, []);

    const handleDismissManualLinkModal = useCallback(() => {
        setSelectedCentral(null);
        setSelectedProvider(null);
    }, []);

    const handleConfirmManualLink = useCallback(() => {
        const central = selectedCentral;
        const provider = selectedProvider;
        const prevResult = runResultRef.current;
        if (!central || !provider || !prevResult) return;
        const pair: DataMatchPair = {
            central: { ...central },
            provider: { ...provider },
        };
        const pk = pairRowKey(pair);
        manualPairsPreserveRef.current = manualPairsPreserveRef.current.filter((p) => pairRowKey(p) !== pk);
        manualPairsPreserveRef.current = [...manualPairsPreserveRef.current, clonePair(pair)];
        setHasManualLinks(true);
        setMatchRunHistoryStack((h) => [
            ...h,
            { runResult: cloneRunResult(prevResult), matchRows: cloneMatchRows(matchRowsRef.current) },
        ]);
        setRunResult((r) => (r ? mergeManualLinksIntoResult(r, [pair]) : r));
        setHasCompletedMatchAttempt(true);
        setDataMatchRunKey((k) => k + 1);
        setSelectedCentral(null);
        setSelectedProvider(null);
    }, [selectedCentral, selectedProvider]);

    // ─── Complete ───────────────────────────────────────────────────────────────

    const handleConfirmComplete = useCallback(async () => {
        const rr = runResultRef.current;
        if (!rr || !canEditEstablishments) return;
        setCompleteSubmitting(true);
        try {
            const rows = matchRowsRef.current;
            if (rows.length === 0) {
                ServiceManager.ToastService.showWarning("Add at least one match rule before completing.");
                return;
            }
            const hasIncompleteRule = rows.some(
                (r) => !String(r.centralColumn ?? "").trim() || !String(r.providerColumn ?? "").trim(),
            );
            if (hasIncompleteRule) {
                ServiceManager.ToastService.showWarning("Select MIS Central and provider columns for each match rule before completing.");
                return;
            }
            syncManualPairsPreserveRefToRunResult(manualPairsPreserveRef, rr);
            const manualCount = manualPairsPreserveRef.current.length;
            const payload = buildCompletePayload(match.clientID, match.misConfigurationID, rr, manualCount, rows);
            const resp = await ServiceManager.EstablishmentService.CompleteDataMatch(establishmentId, payload);
            if (axios.isAxiosError(resp)) {
                const d = resp.response?.data;
                const msg =
                    typeof d === "string"
                        ? d
                        : (d as { title?: string; detail?: string })?.title ??
                        (d as { title?: string; detail?: string })?.detail ??
                        "Data match could not be saved.";
                ServiceManager.ToastService.showError(msg);
                return;
            }
            setCompleteModalOpen(false);
            ServiceManager.ToastService.showSuccess("Data match completed and saved.");
            onBack();
        } catch {
            ServiceManager.ToastService.showError("Data match could not be saved.");
        } finally {
            setCompleteSubmitting(false);
        }
    }, [canEditEstablishments, establishmentId, match.clientID, match.misConfigurationID, onBack]);

    return {
        // State
        matchRows,
        matchRunning,
        runResult,
        hasCompletedMatchAttempt,
        previewLoading,
        dataMatchRunKey,
        openDropdownId,
        setOpenDropdownId,
        hasManualLinks,
        completeModalOpen,
        setCompleteModalOpen,
        abandonModalOpen,
        setAbandonModalOpen,
        completeSubmitting,
        selectedCentral,
        selectedProvider,
        canEditEstablishments,

        // Derived
        canUndoLastMatch,
        showManualLinkModal,

        // Actions
        updateRow,
        removeRow,
        addRow,
        performDataMatch,
        unmatchPairAt,
        undoLastMatch,
        handleSelectCentral,
        handleSelectProvider,
        handleDismissManualLinkModal,
        handleConfirmManualLink,
        handleConfirmComplete,
    };
}
